import Box from "@mui/material/Box";
import { Fab, Tooltip } from "@mui/material";
import { useEffect, useState } from "react";

import Main from "./main";
import Header from "./header";
import NavMini from "./nav-mini";
import NavVertical from "./nav-vertical";
import NavHorizontal from "./nav-horizontal";

import { useBoolean } from "../../hooks/useBoolean";
import { useResponsive } from "../../hooks/useResponsive";
import { useSettingsContext } from "../../components/shared-components/settings";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { getUserModulePrivileges } from "../../modules/auth/userMenuSlice";
import LoadingScreen from "../../components/shared-components/loading-screen";
import WorkList from "src/modules/nurse/work-list";
import AiChat from "src/modules/nurse/ai-chat";
import { useTranslate } from "src/locales";

type Props = {
  children: React.ReactNode;
};

export default function DashboardLayout({ children }: Props) {
  const [workListOpen, setWorkListOpen] = useState(false);
  const [aiChatOpen, setAiChatOpen] = useState(false);

  const settings = useSettingsContext();
  const lgUp = useResponsive("up", "lg");
  const nav = useBoolean();

  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const userMenus = useAppSelector((state) => state.userMenus);
  const { t } = useTranslate();

  useEffect(() => {
    const fetchUserModules = async () => {
      if (isAuthenticated) {
        try {
          await dispatch(getUserModulePrivileges());
        } catch (error) {
          console.error("Failed to fetch user modules:", error);
        }
      }
    };
    fetchUserModules();
  }, [isAuthenticated, dispatch]);

  if (userMenus.loading) {
    return <LoadingScreen />;
  }

  if (userMenus.error) {
    return (
      <Box p={3} color="error.main">
        Error: {userMenus.error}
      </Box>
    );
  }

  if (!userMenus.data || userMenus.data.length === 0) {
    return <Box p={3}>No modules assigned to this user.</Box>;
  }

  const renderNavMini = <NavMini />;
  const renderHorizontal = <NavHorizontal />;
  const renderNavVertical = (
    <NavVertical openNav={nav.value} onCloseNav={nav.onFalse} />
  );

  return (
    <>
      <Header onOpenNav={nav.onTrue} />
      {settings.themeLayout === "horizontal" ? (
        <>
          {lgUp ? renderHorizontal : renderNavVertical}
          <Main>{children}</Main>
        </>
      ) : (
        <Box
          sx={{
            minHeight: 1,
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
          }}
        >
          {lgUp && settings.themeLayout === "mini"
            ? renderNavMini
            : renderNavVertical}
          <Main>{children}</Main>
        </Box>
      )}

      <Tooltip title={t("common.work-list")} placement="left">
        <Fab
          sx={{
            position: "fixed",
            right: { xs: 16, md: 60 },
            bottom: { xs: 110, md: 120 },
            zIndex: 1199,
            boxShadow: "0px 8px 24px rgba(33, 150, 243, 0.35)",
            width: 56,
            height: 56,
            minHeight: 56,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 0,
            bgcolor: "rgba(25, 111, 247, 0.30)",
            border: "none",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              bgcolor: "rgba(25, 111, 247, 0.35)",
              transform: "translateY(-2px)",
              boxShadow: "0px 12px 32px rgba(33, 150, 243, 0.45)",
            },
          }}
          onClick={() => setWorkListOpen(true)}
        >
          <img
            src="/assets/icons/pages/nurse/work-list.svg"
            alt="work-list"
            style={{ width: 28, height: 28 }}
          />
        </Fab>
      </Tooltip>

      <Tooltip title="Yapay Zeka Asistanım" placement="left">
        <Box
          component="button"
          type="button"
          onClick={() => setAiChatOpen((prev) => !prev)}
          sx={{
            position: "fixed",
            right: { xs: 16, md: 60 },
            bottom: { xs: 34, md: 44 },
            zIndex: 1201,
            width: 56,
            height: 56,
            minWidth: 56,
            minHeight: 56,
            p: 0,
            borderRadius: "50%",
            border: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            backgroundColor: "#EEF7FF",
            boxShadow: "0px 12px 30px rgba(91, 163, 235, 0.24)",
            transition: "all 0.2s ease",
            "&:hover": {
              transform: "translateY(-2px)",
              backgroundColor: "#EEF7FF",
              boxShadow: "0px 16px 34px rgba(91, 163, 235, 0.3)",
            },
          }}
        >
          <img
            src="/assets/icons/ai/ai-nurse-icon.png"
            alt="ai-assistant"
            style={{ width: 28, height: 28, objectFit: "contain" }}
          />
        </Box>
      </Tooltip>

      <WorkList open={workListOpen} onClose={() => setWorkListOpen(false)} />
      <AiChat open={aiChatOpen} onClose={() => setAiChatOpen(false)} />
    </>
  );
}
